import { Component, type ReactNode } from 'react';
import type { TranslationKey } from '../i18n.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Les trois états d'un chargement (Charte §5) : `loading`, `loaded`, `error-with-retry`.
 * Pas de rotative éternelle quand le serveur est tombé — un message net et un bouton.
 */
export function Loading({ t }: { t: T }) {
  return (
    <div className="flex items-center justify-center gap-3 p-12 text-muted" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-transparent" />
      {t('state.loading')}
    </div>
  );
}

export function ErrorState({ t, code, onRetry }: { t: T; code: string; onRetry: () => void }) {
  const key = `error.${code}` as TranslationKey;
  const detail = t(key) === key ? t('error.INTERNAL') : t(key);
  return (
    <div className="flex flex-col items-center gap-4 p-12 text-center" role="alert">
      <p className="font-medium text-fg">{t('state.error')}</p>
      <p className="text-sm text-muted">{detail}</p>
      <button type="button" className="btn-secondary" onClick={onRetry}>
        {t('state.retry')}
      </button>
    </div>
  );
}

/** Un état vide se dit en toutes lettres : une zone blanche ne se distingue pas d'un échec. */
export function EmptyState({ message }: { message: string }) {
  return <p className="p-12 text-center text-muted">{message}</p>;
}

/**
 * Aucun bogue imprévu ne doit produire un écran blanc (Charte §5).
 * Les libellés sont injectés : le garde-fou ne doit pas dépendre de ce qu'il protège.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; title: string; body: string; reload: string },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-xl font-semibold">{this.props.title}</h1>
        <p className="max-w-md text-muted">{this.props.body}</p>
        <button type="button" className="btn-primary" onClick={() => location.reload()}>
          {this.props.reload}
        </button>
      </div>
    );
  }
}
